/**
 * @file index.tsx
 *
 * @fileoverview The Job teaser component.
 */

import { cva, VariantProps } from "class-variance-authority";
import { ReactNode } from "react";
import BadgeIcon from "@jq-icons/Badge";
import CalendarIcon from "@jq-icons/Calendar";
import HomeIcon from "@jq-icons/Home";
import MapMarkerIcon from "@jq-icons/MapMarker";
import OfficeIcon from "@jq-icons/Office";
import { JobDate } from "../../../../models/job";
import FlexItems from "../../../02-atoms/06-layout/03-flex";
import FlexItem from "../../../02-atoms/06-layout/03-flex/02-flex-item";
import PopoverList from "../../../03-molecules/13-popover-list";
import styles from "./jobTeaser.module.scss";
import HiddenVisually from "../../../02-atoms/14-w-a11y/02-visually-hidden";
import LightningIcon from "@jq-icons/Lightning";
import WalletIcon from "@jq-icons/Wallet";
import TrophyIcon from "@jq-icons/Trophy";
import SourcePortals from "../../../03-molecules/16-source-portals";

const jobTeaserWrapper = cva([styles.jobTeaserWrapper], {
  variants: {
    active: {
      true: styles.jobTeaserWrapperActive,
    },
    featured: {
      true: styles.jobTeaserWrapperFeatured,
    },
    inactive: {
      true: styles.jobTeaserWrapperInactive,
    },
    topJob: {
      true: styles.jobTeaserWrapperTopJob,
    },
  },
  defaultVariants: {
    active: false,
    inactive: false,
    featured: false,
    topJob: false,
  },
});

const jobTeaserTitle = cva([styles.jobTeaserTitle], {
  variants: {
    active: {
      true: styles.jobTeaserTitleActive,
    },
  },
});

const jobTeaserCompany = cva([styles.jobTeaserCompany], {
  variants: {
    active: {
      true: styles.jobTeaserCompanyActive,
    },
  },
});

const jobTeaserLocation = cva([styles.jobTeaserLocation], {
  variants: {
    active: {
      true: styles.jobTeaserLocationActive,
    },
    jobTeaserIconHideMobile: {
      true: styles.jobTeaserIconHideMobile,
    },
  },
});

const jobTeaserRemote = cva([styles.jobTeaserRemote], {
  variants: {
    active: {
      true: styles.jobTeaserRemoteActive,
    },
  },
});

const jobTeaserOccupation = cva([styles.jobTeaserOccupation], {
  variants: {
    active: {
      true: styles.jobTeaserOccupationActive,
    },
  },
});

const jobTeaserSalary = cva([styles.jobTeaserSalary], {
  variants: {
    active: {
      true: styles.jobTeaserSalaryActive,
    },
  },
});

const jobTeaserDescription = cva([styles.jobTeaserDescription], {
  variants: {
    active: {
      true: styles.jobTeaserDescriptionActive,
    },
  },
});

const jobTeaserDate = cva([styles.jobTeaserDate], {
  variants: {
    active: {
      true: styles.jobTeaserDateActive,
    },
  },
});

const jobTeaserIcon = cva([styles.jobTeaserIcon], {
  variants: {
    active: {
      true: styles.jobTeaserIconActive,
    },
    premiumCompany: {
      true: styles.jobTeaserIconPremiumCompany,
    },
    jobTeaserIconHideMobile: {
      true: styles.jobTeaserIconHideMobile,
    },
  },
  defaultVariants: {
    premiumCompany: false,
    jobTeaserIconHideMobile: true,
  },
});

export interface JobTeaserProps
  extends VariantProps<typeof jobTeaserWrapper>,
    VariantProps<typeof jobTeaserIcon> {
  /**
   * The teaser image.
   */
  image?: ReactNode;
  /**
   * The job title component.
   */
  title: ReactNode;
  /**
   * The company name.
   */
  company?: string | ReactNode;
  /**
   * The locations for the job.
   */
  locations: string[];
  /**
   * Job description (trimmed).
   */
  description?: string;
  /**
   * List of occupation names.
   */
  occupations?: string[];
  /**
   * The job salary.
   */
  salary?: string | ReactNode;
  /**
   * The tooltip text.
   */
  tooltip?: string;
  /**
   * The job date.
   */
  dates?: JobDate[];
  /**
   * If the job is remote the remote options for the job.
   */
  remote?: string | ReactNode;
  /**
   * The bookmark component.
   *
   * @default null
   */
  Bookmark?: ReactNode;
  /**
   * The label for the featured badge.
   *
   * @default ""
   */
  featuredLabel?: string;
  /**
   * The featured job icon.
   */
  featuredJobIcon?: ReactNode;
  /**
   * The optional custom badge component.
   */
  customBadge?: ReactNode;
  /**
   * Show icons for each of the job information.
   *
   * @default "true"
   */
  hideIconsMobile?: boolean;
  /**
   * The quick apply text.
   *
   * @default undefined
   */
  quickApplyText?: string;
  /**
   * Signifies if the job is marked as a top job.
   *
   * @default "false"
   */
  topJob?: boolean;
  /**
   * The top job label.
   *
   * @default ""
   */
  topJobLabel?: string;
  /**
   * The icon for the top job.
   */
  topJobIcon?: ReactNode;
  /**
   * The source portals for the job.
   */
  sourcePortals?: string[];
  /**
   * The organization icon for the job.
   */
  organizationIcon?: ReactNode;
  /**
   * The promoted organization icon for the job.
   */
  promotedOrganizationIcon?: ReactNode;
  /**
   * The custom information for the job.
   */
  customInfo?: ReactNode;
}

/**
 * The job teaser presents information about a job in a condensed way.
 */
export default function JobTeaser({
  image,
  title,
  company,
  locations,
  occupations,
  salary,
  description,
  active,
  featured = false,
  inactive = false,
  tooltip,
  dates,
  remote,
  Bookmark = null,
  premiumCompany = false,
  featuredLabel = "",
  featuredJobIcon,
  customBadge,
  hideIconsMobile = true,
  quickApplyText,
  topJob = false,
  topJobLabel = "",
  topJobIcon,
  sourcePortals = [],
  organizationIcon,
  promotedOrganizationIcon,
  customInfo,
}: JobTeaserProps) {
  const TopJobIconElement = topJobIcon ?? (
    <TrophyIcon title={topJobLabel} width="25" height="25" />
  );
  const FeaturedJobIconElement = featuredJobIcon ?? (
    <BadgeIcon title={featuredLabel} width="25" height="25" />
  );
  const OrganizationIcon = organizationIcon ?? <OfficeIcon />;
  const PromotedOrgIcon = promotedOrganizationIcon ?? <BadgeIcon />;

  return (
    <div
      className={`${jobTeaserWrapper({
        active,
        featured,
        inactive,
        topJob,
      })} jcl-job-teaser ${featured ? 'jcl-job-teaser-featured' : ''} ${topJob ? 'jcl-job-teaser-top-job' : ''}`}
      data-testid="jcl-job-teaser-wrapper"
      title={tooltip}
    >
      <div className="jcl-job-teaser-image">{image && image}</div>
      <div className="jcl-job-teaser-content">
        <div className={`${jobTeaserTitle({ active })} jcl-job-teaser-title`}>
          {title}
        </div>
        <div className="jcl-job-teaser-info">
          {company && (
            <div
              className={`${jobTeaserCompany({
                active,
              })} jcl-job-teaser-company`}
            >
              <span
                className={`${jobTeaserIcon({
                  active: active && !premiumCompany,
                  premiumCompany,
                  jobTeaserIconHideMobile: hideIconsMobile,
                })} jcl-job-teaser-icon`}
              >
                {premiumCompany ? PromotedOrgIcon : OrganizationIcon}
              </span>
              <div className={styles.companyName}>{company}</div>
            </div>
          )}
          <FlexItems alignItems="center">
            {locations.length > 0 && (
              <div
                className={`${jobTeaserLocation({
                  active,
                })} jcl-job-teaser-location`}
                data-testid="jcl-job-teaser-location"
              >
                <PopoverList
                  items={locations}
                  iconGap={0}
                  Icon={
                    <span
                      className={`${jobTeaserIcon({
                        jobTeaserIconHideMobile: hideIconsMobile,
                      })}`}
                    >
                      <MapMarkerIcon title="location" />
                    </span>
                  }
                />
              </div>
            )}
            {remote && (
              <div
                className={`${jobTeaserRemote({
                  active,
                })} jcl-job-teaser-remote`}
              >
                <span
                  className={`${jobTeaserIcon({
                    active,
                    jobTeaserIconHideMobile: hideIconsMobile,
                  })} jcl-job-teaser-icon`}
                >
                  <HomeIcon title="remote" />
                </span>
                {remote}
              </div>
            )}
            {dates?.map((date, index) => (
              <div className={jobTeaserDate({ active })} key={index}>
                <span
                  className={jobTeaserIcon({
                    active,
                    jobTeaserIconHideMobile: hideIconsMobile,
                  })}
                >
                  {date.icon ? (
                    date.icon
                  ) : (
                    <CalendarIcon title={date.title ?? "Date"} />
                  )}
                </span>
                <HiddenVisually>
                  {date.title}
                  {`:`}&nbsp;
                </HiddenVisually>
                {date.text}
              </div>
            ))}
            {salary && (
              <div
                className={`${jobTeaserSalary({
                  active,
                })} jcl-job-teaser-salary`}
              >
                <span
                  className={`${jobTeaserIcon({
                    active,
                    jobTeaserIconHideMobile: hideIconsMobile,
                  })} jcl-job-teaser-icon`}
                >
                  <WalletIcon title="salary" />
                </span>
                {salary}
              </div>
            )}
            {customInfo && customInfo}
          </FlexItems>
        </div>
        <div
          className={`${jobTeaserOccupation({
            active,
          })} jcl-job-teaser-occupation`}
        >
          {occupations && occupations.join(" | ")}
        </div>
        {quickApplyText && (
          <FlexItems gap={3}>
            <FlexItem marginTop={2}>
              <LightningIcon color="featured" />
            </FlexItem>
            <FlexItem className={styles.quickApplyText}>
              {quickApplyText}
            </FlexItem>
          </FlexItems>
        )}
        <div
          className={`${jobTeaserDescription({
            active,
          })} jcl-job-teaser-description`}
        >
          {description}
        </div>
        {sourcePortals && sourcePortals.length > 0 && (
          <SourcePortals sourcePortals={sourcePortals} />
        )}
      </div>
      {(featured || customBadge || Bookmark || topJob) && (
        <>
          <FlexItem flex="1" />
          <FlexItems
            alignItems="flex-start"
            gap={5}
            flexDirection={["column", "row"]}
            justifyContent="space-between"
            flexWrap="nowrap"
            className={styles.jobTeaserActions}
          >
            <div
              className={`${styles.featuredIcon} ${
                topJob
                  ? styles.topJobIcon
                  : featured
                    ? styles.featuredBadgeIcon
                    : ""
              } ${
                topJob
                  ? "jcl-job-teaser-top-job-icon"
                  : featured
                    ? "jcl-job-teaser-featured-icon"
                    : ""
              }`}
            >
              {topJob ? TopJobIconElement : featured && FeaturedJobIconElement}
            </div>
            {customBadge}
            {Bookmark && (
              <span className={styles.bookmarkIcon}>{Bookmark}</span>
            )}
          </FlexItems>
        </>
      )}
    </div>
  );
}

export { JobTeaser };
